import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Button } from 'tdesign-react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import styles from './3dViewer.module.less';

export interface Model3DViewerProps {
  bgImage?: string | Blob; // 新增背景图属性
}

export interface Model3DViewerRef {
  captureScreenshot: () => Promise<Blob>;
}

export const Model3DViewer = forwardRef<Model3DViewerRef, Model3DViewerProps>(({
  bgImage,
}, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Three.js相关引用
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const modelRef = useRef<THREE.Group | null>(null);
  const animationRef = useRef<number>(0);
  const bgTextureRef = useRef<THREE.Texture | null>(null);

  // 暴露给父组件的方法
  useImperativeHandle(ref, () => ({
    captureScreenshot,
  }));

  // 加载背景图
  // const loadBackgroundImage = async () => {
  //   if (!bgImage || !sceneRef.current || !canvasRef.current) return;

  //   try {
  //     const textureLoader = new THREE.TextureLoader();
  //     let texture: THREE.Texture;

  //     if (typeof bgImage === 'string') {
  //       texture = await new Promise<THREE.Texture>((resolve, reject) => {
  //         textureLoader.load(bgImage, resolve, undefined, reject);
  //       });
  //     } else {
  //       const url = URL.createObjectURL(bgImage);
  //       texture = await new Promise<THREE.Texture>((resolve, reject) => {
  //         textureLoader.load(url, resolve, undefined, reject);
  //       });
  //       URL.revokeObjectURL(url);
  //     }

  //     // 存储纹理引用
  //     bgTextureRef.current = texture;

  //     // 直接设置为场景背景
  //     sceneRef.current.background = texture;
  //     texture.colorSpace = THREE.SRGBColorSpace;

  //     // 立即更新一次纹理比例
  //     updateBackgroundAspect();
  //   } catch (error) {
  //     console.error('背景图加载失败:', error);
  //   }
  // };

  // 更新背景纹理比例
  const updateBackgroundAspect = () => {
    if (!bgTextureRef.current || !canvasRef.current || !bgTextureRef.current.image) return;

    const texture = bgTextureRef.current;
    const canvas = canvasRef.current;
    const aspect = canvas.clientWidth / canvas.clientHeight;

    THREE.TextureUtils.contain(texture, aspect);

    // 需要通知 Three.js 纹理已更新
    texture.needsUpdate = true;
  };

  // 动画循环
  const animate = () => {
    animationRef.current = requestAnimationFrame(animate);
    controlsRef.current?.update();
    rendererRef.current?.render(sceneRef.current!, cameraRef.current!);
  };

  // 初始化Three.js场景
  const initScene = () => {
    if (!canvasRef.current) return;

    // 创建场景
    const scene = new THREE.Scene();
    scene.background = null; // 初始化为透明背景
    sceneRef.current = scene;

    // 创建相机
    const camera = new THREE.PerspectiveCamera(
      75,
      canvasRef.current.clientWidth / canvasRef.current.clientHeight,
      0.1,
      1000,
    );
    camera.position.z = 5;
    cameraRef.current = camera;

    // 创建渲染器
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true, // 保留绘制缓冲区用于截图
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setSize(canvasRef.current.clientWidth, canvasRef.current.clientHeight);
    rendererRef.current = renderer;

    // 添加光源
    const ambientLight = new THREE.AmbientLight(0x404040, 1);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    // 添加轨道控制器
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controlsRef.current = controls;

    // 开始动画循环
    animate();

    // 加载背景图
    // loadBackgroundImage();
  };

  // 加载GLB模型
  const loadModel = (file: File) => {
    if (!sceneRef.current) return;

    // 清理旧模型
    if (modelRef.current) {
      sceneRef.current.remove(modelRef.current);
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const arrayBuffer = event.target?.result as ArrayBuffer;

      const loader = new GLTFLoader();
      loader.parse(
        arrayBuffer,
        '',
        (gltf) => {
          modelRef.current = gltf.scene;
          sceneRef.current?.add(modelRef.current);

          // 自动调整相机位置
          const box = new THREE.Box3().setFromObject(modelRef.current);
          const size = box.getSize(new THREE.Vector3()).length();
          const center = box.getCenter(new THREE.Vector3());

          if (cameraRef.current) {
            cameraRef.current.position.copy(center);
            cameraRef.current.position.z += size * 1.5;
            controlsRef.current?.update();
          }
        },
        (error) => {
          console.error('模型加载失败:', error);
        },
      );
    };
    reader.readAsArrayBuffer(file);
  };

  // 处理文件选择
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file?.name.endsWith('.glb')) {
      loadModel(file);
    }
  };

  // 生成2D截图（返回Blob）
  const captureScreenshot = (): Promise<Blob> => new Promise((resolve, reject) => {
    if (!rendererRef.current || !cameraRef.current || !sceneRef.current) {
      return reject(new Error('渲染器未初始化'));
    }

    // 确保场景已渲染
    rendererRef.current.render(sceneRef.current, cameraRef.current);

    // 获取原始画布尺寸
    const { width, height } = rendererRef.current.getSize(new THREE.Vector2());

    // 创建临时canvas
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;

    const context = tempCanvas.getContext('2d');
    if (!context) {
      return reject(new Error('无法获取2D上下文'));
    }

    // 将WebGL内容绘制到2D画布
    context.drawImage(rendererRef.current.domElement, 0, 0, width, height);

    // 转换为Blob[3](@ref)
    tempCanvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('生成Blob失败'));
        }
      },
      'image/png',
      1, // 最高质量
    );
  });

  // 导出2D平面图
  const handleExport2D = async () => {
    try {
      const blob = await captureScreenshot();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'model_2d.png';
      document.body.appendChild(a);
      a.click();

      // 清理资源
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('导出失败:', error);
    }
  };

  // 清理资源
  const cleanup = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (rendererRef.current) {
      rendererRef.current.dispose();
    }
    if (controlsRef.current) {
      controlsRef.current.dispose();
    }
  };

  // 窗口大小变化时调整渲染器
  const handleResize = () => {
    if (canvasRef.current && rendererRef.current && cameraRef.current) {
      cameraRef.current.aspect = canvasRef.current.clientWidth / canvasRef.current.clientHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(
        canvasRef.current.clientWidth,
        canvasRef.current.clientHeight,
      );

      // 窗口大小变化时更新背景比例
      updateBackgroundAspect();
    }
  };

  useEffect(() => {
    initScene();

    window.addEventListener('resize', handleResize);
    return () => {
      cleanup();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 监听背景图变化
  // useEffect(() => {
  //   if (sceneRef.current && bgImage) {
  //     loadBackgroundImage();
  //   }
  // }, [bgImage]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.op}>
        <Button
          onClick={() => fileInputRef.current?.click()}
        >
          上传GLB文件
        </Button>
        <Button
          onClick={handleExport2D}
          style={{ marginLeft: '10px' }}
        >
          导出2D平面图
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          accept=".glb"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      <div
        className={styles.canvas}
        style={{
          backgroundImage: `url(${bgImage as string})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
      </div>
    </div>
  );
});
